import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES, buildTelehealthWaitingPath } from "@/routes";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { displayDate, displayTime } from "@/lib/timeUtils";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Video, 
  Loader2,
  User,
  XCircle,
  CheckCircle2,
  ChevronRight,
  AlertTriangle,
  Star,
  Info
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { RateProviderDialog } from "@/components/telehealth/RateProviderDialog";
interface Appointment {
  id: string;
  scheduled_date: string;
  scheduled_time: string;
  visit_type: string;
  visit_reason: string | null;
  status: string;
  provider_id: string;
  duration_minutes: number | null;
  zoom_join_url?: string | null;
  zoom_status?: string | null;
  provider_name?: string | null;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  scheduled: { label: "Scheduled", variant: "default" },
  waiting: { label: "In Waiting Room", variant: "secondary" },
  in_progress: { label: "In Progress", variant: "default" },
  completed: { label: "Completed", variant: "outline" },
  cancelled: { label: "Cancelled", variant: "destructive" },
  no_show: { label: "No Show", variant: "destructive" },
};

export default function MemberAppointments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [ratingSession, setRatingSession] = useState<{ sessionId: string; providerId: string; providerName: string } | null>(null);
  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);

  // Fetch existing ratings for this member
  const { data: existingRatings } = useQuery({
    queryKey: ["member-ratings", user?.id],
    queryFn: async () => {
      if (!user) return {};
      const { data, error } = await supabase
        .from("provider_ratings" as any)
        .select("session_id, rating, comment")
        .eq("member_id", user.id);
      if (error) return {};
      const map: Record<string, { rating: number; comment: string | null }> = {};
      (data || []).forEach((r: any) => {
        map[r.session_id] = { rating: r.rating, comment: r.comment };
      });
      return map;
    },
    enabled: !!user,
  });

  // Fetch appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!user) return;

      setIsLoading(true);
      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("id, scheduled_date, scheduled_time, visit_type, visit_reason, status, provider_id, duration_minutes, zoom_join_url, zoom_status")
        .eq("member_id", user.id)
        .order("scheduled_date", { ascending: true })
        .order("scheduled_time", { ascending: true });

      if (error) {
        console.error("Error fetching appointments:", error);
        toast.error("Failed to load appointments");
      } else {
        // Batch-fetch provider names from providers table (accessible via RLS)
        const providerIds = [...new Set((data || []).map((a: any) => a.provider_id).filter(Boolean))];
        let providerMap: Record<string, string> = {};
        if (providerIds.length > 0) {
          const { data: providers } = await supabase
            .from("providers")
            .select("user_id, display_name")
            .in("user_id", providerIds);
          (providers || []).forEach((p: any) => {
            providerMap[p.user_id] = p.display_name;
          });
        }
        setAppointments(
          (data || []).map((a: any) => ({
            ...a,
            provider_name: providerMap[a.provider_id] || null,
          }))
        );
      }
      setIsLoading(false);
    };

    fetchAppointments();

    // Subscribe to realtime updates
    const channel = supabase
      .channel("member-appointments")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "telehealth_sessions",
          filter: `member_id=eq.${user?.id}`,
        },
        () => {
          fetchAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel || !user) return;

    setIsCancelling(true);
    
    // Update the session status to cancelled
    const { error } = await supabase
      .from("telehealth_sessions")
      .update({ status: "cancelled" })
      .eq("id", appointmentToCancel);

    if (error) {
      console.error("Error cancelling appointment:", error);
      toast.error("Failed to cancel appointment");
      setIsCancelling(false);
      return;
    }

    // Notify the provider about the cancellation
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-cancellation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            sessionId: appointmentToCancel,
            reason: cancellationReason || undefined,
            memberId: user.id,
          }),
        }
      );

      if (!response.ok) {
        console.error("Failed to notify provider:", await response.text());
      }
    } catch (notifyError) {
      console.error("Error notifying provider:", notifyError);
      // Don't fail the cancellation if notification fails
    }

    toast.success("Appointment cancelled successfully");
    setAppointments(
      appointments.map((a) =>
        a.id === appointmentToCancel ? { ...a, status: "cancelled" } : a
      )
    );
    
    setIsCancelling(false);
    setCancelDialogOpen(false);
    setAppointmentToCancel(null);
    setCancellationReason("");
  };

  const formatTime = (timeString: string, dateString?: string) => {
    if (dateString) {
      return displayTime(timeString, dateString);
    }
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const upcomingAppointments = appointments.filter(
    (a) => ["scheduled", "waiting", "in_progress"].includes(a.status) && !moment.utc(a.scheduled_date).isBefore(moment.utc(), "day")
  );
  
  const pastAppointments = appointments.filter(
    (a) => ["completed", "cancelled", "no_show"].includes(a.status) || 
           (a.status === "scheduled" && moment.utc(a.scheduled_date).isBefore(moment.utc(), "day"))
  );

  const canJoinSession = (appointment: Appointment) => {
    const appointmentDate = moment.utc(appointment.scheduled_date);
    const today = moment.utc().startOf("day");
    return (
      (appointment.status === "scheduled" || appointment.status === "waiting" || appointment.status === "in_progress") &&
      (appointmentDate.isSame(today, "day") || appointmentDate.isBefore(today, "day"))
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MemberSidebar />
        <div className="ml-64">
          <MemberHeader />
          <main className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="mt-2 text-muted-foreground">Loading appointments...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="ml-64">
        <MemberHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground">My Appointments</h1>
              <p className="mt-1 text-muted-foreground">
                View and manage your telehealth appointments
              </p>
            </div>
            <Button onClick={() => navigate(ROUTES.MEMBER_SCHEDULE)} className="gap-2">
              <CalendarIcon className="h-4 w-4" />
              Schedule New Visit
            </Button>
          </div>

          <Tabs defaultValue="upcoming" className="space-y-6">
            <TabsList>
              <TabsTrigger value="upcoming" className="gap-2">
                <Video className="h-4 w-4" />
                Upcoming ({upcomingAppointments.length})
              </TabsTrigger>
              <TabsTrigger value="past" className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Past ({pastAppointments.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming" className="space-y-4">
              {upcomingAppointments.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Upcoming Appointments</h3>
                    <p className="text-muted-foreground mb-4">
                      You don't have any scheduled telehealth visits.
                    </p>
                    <Button onClick={() => navigate(ROUTES.MEMBER_SCHEDULE)}>
                      Schedule Your First Visit
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                upcomingAppointments.map((appointment) => (
                  <Card key={appointment.id} className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="flex items-stretch">
                        {/* Date sidebar */}
                        <div className="bg-primary/5 p-6 flex flex-col items-center justify-center min-w-[120px] border-r">
                          <span className="text-3xl font-bold text-primary">
                            {displayDate(appointment.scheduled_date, "D")}
                          </span>
                          <span className="text-sm font-medium text-muted-foreground">
                            {displayDate(appointment.scheduled_date, "MMM YYYY")}
                          </span>
                          <span className="text-xs text-muted-foreground mt-1">
                            {displayDate(appointment.scheduled_date, "dddd")}
                          </span>
                        </div>

                        {/* Main content */}
                        <div className="flex-1 p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-4">
                              <Avatar className="h-12 w-12">
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  <User className="h-5 w-5" />
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h3 className="font-semibold text-foreground">
                                  {appointment.provider_name ? `Dr. ${appointment.provider_name}` : "Telehealth Visit"}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {appointment.visit_reason || "General consultation"}
                                </p>
                              </div>
                            </div>
                            <Badge variant={statusConfig[appointment.status]?.variant || "outline"}>
                              {statusConfig[appointment.status]?.label || appointment.status}
                            </Badge>
                          </div>

                          <div className="mt-4 flex items-center gap-6 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>{formatTime(appointment.scheduled_time, appointment.scheduled_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Video className="h-4 w-4" />
                              <span>{appointment.duration_minutes || 30} minutes</span>
                            </div>
                          </div>

                          <div className="mt-4 flex items-center gap-3">
                            {canJoinSession(appointment) && appointment.zoom_join_url && (
                              <Button
                                onClick={() => window.open(appointment.zoom_join_url!, "_blank")}
                                className="gap-2"
                              >
                                <Video className="h-4 w-4" />
                                Join Zoom Meeting
                              </Button>
                            )}
                            {canJoinSession(appointment) && !appointment.zoom_join_url && (
                              <Button
                                onClick={() => navigate(buildTelehealthWaitingPath(appointment.id))}
                                className="gap-2"
                              >
                                <Video className="h-4 w-4" />
                                Join Waiting Room
                              </Button>
                            )}
                            {appointment.status === "scheduled" && (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setAppointmentToCancel(appointment.id);
                                  setCancelDialogOpen(true);
                                }}
                                className="gap-2 text-destructive hover:text-destructive"
                              >
                                <XCircle className="h-4 w-4" />
                                Cancel
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDetailAppointment(appointment)}
                              className="gap-2"
                            >
                              <Info className="h-4 w-4" />
                              View Details
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>

            <TabsContent value="past" className="space-y-4">
              {pastAppointments.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Past Appointments</h3>
                    <p className="text-muted-foreground">
                      Your completed appointments will appear here.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                pastAppointments.map((appointment) => (
                  <Card key={appointment.id} className={cn(
                    "overflow-hidden",
                    appointment.status === "cancelled" && "opacity-60"
                  )}>
                    <CardContent className="p-0">
                      <div className="flex items-stretch">
                        {/* Date sidebar */}
                        <div className="bg-muted/50 p-6 flex flex-col items-center justify-center min-w-[120px] border-r">
                          <span className="text-3xl font-bold text-muted-foreground">
                            {displayDate(appointment.scheduled_date, "D")}
                          </span>
                          <span className="text-sm font-medium text-muted-foreground">
                            {displayDate(appointment.scheduled_date, "MMM YYYY")}
                          </span>
                        </div>

                        {/* Main content */}
                        <div className="flex-1 p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-4">
                              <Avatar className="h-12 w-12">
                                <AvatarFallback className="bg-muted text-muted-foreground">
                                  <User className="h-5 w-5" />
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h3 className="font-semibold text-foreground">
                                  {appointment.provider_name ? `Dr. ${appointment.provider_name}` : "Telehealth Visit"}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {appointment.visit_reason || "General consultation"}
                                </p>
                              </div>
                            </div>
                            <Badge variant={statusConfig[appointment.status]?.variant || "outline"}>
                              {statusConfig[appointment.status]?.label || appointment.status}
                            </Badge>
                          </div>

                          <div className="mt-4 flex items-center gap-6 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>{formatTime(appointment.scheduled_time, appointment.scheduled_date)}</span>
                            </div>
                          </div>

                          {/* Rating section for completed visits */}
                          {appointment.status === "completed" && (
                            <div className="mt-4 flex items-center gap-3">
                              {existingRatings?.[appointment.id] ? (
                                <button
                                  onClick={() => {
                                    setRatingSession({
                                      sessionId: appointment.id,
                                      providerId: appointment.provider_id,
                                      providerName: appointment.provider_name ? `Dr. ${appointment.provider_name}` : "your provider",
                                    });
                                    setRatingDialogOpen(true);
                                  }}
                                  className="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:bg-muted/50 transition-colors"
                                >
                                  <div className="flex">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                      <Star
                                        key={s}
                                        className={cn(
                                          "h-4 w-4",
                                          s <= existingRatings[appointment.id].rating
                                            ? "fill-amber-400 text-amber-400"
                                            : "text-muted-foreground/30"
                                        )}
                                      />
                                    ))}
                                  </div>
                                  <span className="text-muted-foreground">Edit rating</span>
                                </button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-2"
                                  onClick={() => {
                                    setRatingSession({
                                      sessionId: appointment.id,
                                      providerId: appointment.provider_id,
                                      providerName: appointment.provider_name ? `Dr. ${appointment.provider_name}` : "your provider",
                                    });
                                    setRatingDialogOpen(true);
                                  }}
                                >
                                  <Star className="h-4 w-4" />
                                  Rate Visit
                                </Button>
                              )}
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetailAppointment(appointment)}
                            className="gap-2"
                          >
                            <Info className="h-4 w-4" />
                            View Details
                          </Button>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center px-4">
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={(open) => {
        setCancelDialogOpen(open);
        if (!open) {
          setCancellationReason("");
        }
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <AlertDialogTitle className="text-xl">Cancel Appointment?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              This will cancel your scheduled telehealth visit and notify your provider.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Label htmlFor="cancellation-reason" className="text-sm font-medium">
              Reason for cancellation (optional)
            </Label>
            <Textarea
              id="cancellation-reason"
              placeholder="Let your provider know why you're cancelling..."
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              className="mt-2 resize-none"
              rows={3}
              disabled={isCancelling}
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelAppointment}
              disabled={isCancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling ? "Cancelling..." : "Cancel Appointment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rate Provider Dialog */}
      {ratingSession && (
        <RateProviderDialog
          open={ratingDialogOpen}
          onOpenChange={(open) => {
            setRatingDialogOpen(open);
            if (!open) setRatingSession(null);
          }}
          sessionId={ratingSession.sessionId}
          providerId={ratingSession.providerId}
          providerName={ratingSession.providerName}
          existingRating={existingRatings?.[ratingSession.sessionId] || null}
        />
      )}

      {/* Appointment Detail Dialog */}
      <Dialog open={!!detailAppointment} onOpenChange={(open) => !open && setDetailAppointment(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
          </DialogHeader>
          {detailAppointment && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <User className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-foreground">
                    {detailAppointment.provider_name ? `Dr. ${detailAppointment.provider_name}` : "Telehealth Visit"}
                  </p>
                  <Badge variant={statusConfig[detailAppointment.status]?.variant || "outline"} className="mt-1">
                    {statusConfig[detailAppointment.status]?.label || detailAppointment.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{displayDate(detailAppointment.scheduled_date, "MMMM D, YYYY")}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Time</p>
                  <p className="font-medium">{formatTime(detailAppointment.scheduled_time, detailAppointment.scheduled_date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p className="font-medium">{detailAppointment.duration_minutes || 30} minutes</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Visit Type</p>
                  <p className="font-medium capitalize">{detailAppointment.visit_type.replace(/_/g, " ")}</p>
                </div>
              </div>

              {detailAppointment.visit_reason && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Reason for Visit</p>
                  <p className="font-medium">{detailAppointment.visit_reason}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {canJoinSession(detailAppointment) && detailAppointment.zoom_join_url && (
                  <Button onClick={() => window.open(detailAppointment.zoom_join_url!, "_blank")} className="gap-2">
                    <Video className="h-4 w-4" />
                    Join Zoom Meeting
                  </Button>
                )}
                {canJoinSession(detailAppointment) && !detailAppointment.zoom_join_url && (
                  <Button onClick={() => navigate(buildTelehealthWaitingPath(detailAppointment.id))} className="gap-2">
                    <Video className="h-4 w-4" />
                    Join Waiting Room
                  </Button>
                )}
                {detailAppointment.status === "scheduled" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDetailAppointment(null);
                      setAppointmentToCancel(detailAppointment.id);
                      setCancelDialogOpen(true);
                    }}
                    className="gap-2 text-destructive hover:text-destructive"
                  >
                    <XCircle className="h-4 w-4" />
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
